import { ENTER } from '@angular/cdk/keycodes';
import {
  AfterContentInit,
  ApplicationRef,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  ElementRef,
  EmbeddedViewRef,
  EventEmitter,
  HostListener,
  Injector,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { CustomUserDropdownItem } from '@vendasta/atlas';
import { combineLatest, Observable } from 'rxjs';
import {map, shareReplay, take, tap} from 'rxjs/operators';
import { DropdownItem } from '../atlas_sdk/api';
import { BusinessSidebarComponent } from '../business-sidebar/business-sidebar.component';
import { BusinessNavConfigService } from '../config.service';
import { SidebarService } from '../core-components/sidebar/sidebar.service';
import { SnackbarComponent } from '../core-components/snackbar/snackbar.component';
import { BusinessNavDataService } from '../data.service';
import { Environment } from '../environment';
import { BusinessNavEnvironmentService } from '../environment.service';
import { BusinessNavKeybindService } from '../keybind.service';
import { BusinessNavSessionService } from '../session.service';
import { AtlasTranslateService } from '../translate/translate.service';
import { ModalService } from '../core-components/modal/modal.service';

const AtlasInjectionContainerId = 'business-nav__atlas-injection-items';

@Component({
  selector: 'atlas-business-navbar',
  templateUrl: './navigation.component.html',
  styleUrls: ['./navigation.component.scss']
})
export class NavigationComponent implements OnInit, OnChanges, OnDestroy, AfterContentInit {
  @Input() staticMenu: string | boolean;
  @Input() partnerId: string;
  @Input() accountId: string;
  @Input() marketId: string;
  @Input() brandPath: string;
  @Input() serviceProviderId: string;
  @Input() token: string;
  @Input() demo: string | boolean;
  @Input() hideCenters: string | boolean;
  @Input() hideNotifications: string | boolean;
  @Input() disableUserDropdown: string | boolean;
  @Input() disableNavigation: string | boolean;
  @Input() alwaysShowMenuToggle: string | boolean;
  @Input() showLanguageSelector?: string | boolean = true;
  @Input() defaultURL: string;
  @Output() event: EventEmitter<string> = new EventEmitter<string>();
  @ViewChild('inject', { static: true }) injectItems: ElementRef;

  public _calculatedStaticMenu: boolean;
  public showMenuHamburger$: Observable<boolean>;
  public dropdownItems$: Observable<CustomUserDropdownItem[]>;

  private sidebar: ComponentRef<BusinessSidebarComponent>;
  private snackbar: ComponentRef<SnackbarComponent>;

  menuText$: Observable<string>;

  constructor(private viewContainerRef: ViewContainerRef,
              private componentFactoryResolver: ComponentFactoryResolver,
              private appRef: ApplicationRef,
              private injector: Injector,
              private sidebarService: SidebarService,
              private sessionService: BusinessNavSessionService,
              private environmentService: BusinessNavEnvironmentService,
              private cfgService: BusinessNavConfigService,
              private dataService: BusinessNavDataService,
              private translate: AtlasTranslateService,
              private keybindService: BusinessNavKeybindService,
              private modalService: ModalService,
  ) {
    this.showMenuHamburger$ = this.sidebarService.showMenuToggle$;
    // Init the nav
    this.calcStaticMenu();
    this.refreshSession();
    this.refreshEnvironment();
    this.refreshConfig();
    this.calcDisableNavigation();
    this.calcAlwaysShowMenuToggle();
  }

  ngOnInit(): void {
    this.menuText$ = this.translate.stream('MENU_TOGGLE', 'Menu');
    setTimeout(() => {
      this.dropdownItems$ = combineLatest(this.dataService.dropdownItems$, this.translate.onTranslationChange).pipe(
        map(([dropdownItems, _]: [DropdownItem[], any]) => dropdownItems.map((item: DropdownItem) => {
            const i = {
              label: this.translate.instant(item.translationId, item.label),
            } as CustomUserDropdownItem;

            if (this.serviceProviderId.toUpperCase() === 'VBC' && item.label !== 'Sign Out') {
              i.route = item.path;
            } else {
              i.url = item.url;
            }

            return i;
          })),
        shareReplay(1),
      );
    }, 0);
  }

  ngAfterContentInit(): void {
    setTimeout(() => {
      // Create the Sidebar component
      this.sidebar = this.componentFactoryResolver
        .resolveComponentFactory(BusinessSidebarComponent)
        .create(this.injector);
      // Attach it to the running angular instance
      this.appRef.attachView(this.sidebar.hostView);
      // Place it on the body
      const sidebarDom = (this.sidebar.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
      document.body.appendChild(sidebarDom);

      // Create the Snackbar component
      this.snackbar = this.componentFactoryResolver
        .resolveComponentFactory(SnackbarComponent)
        .create(this.injector);
      // Attach it to the running angular instance
      this.appRef.attachView(this.snackbar.hostView);
      // Place it on the body
      const snackbarDom = (this.snackbar.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
      document.body.appendChild(snackbarDom);

      const children: HTMLElement[] = Array.from(this.injectItems.nativeElement.children);
      // Move the injected dom into the sidebar
      for (const injectionItem of children) {
        if (injectionItem.className === AtlasInjectionContainerId) {
          this.injectItemsIntoAtlas(Array.from(injectionItem.children));
        } else {
          this.sidebar.instance.injectionSpace.nativeElement.appendChild(injectionItem);
        }
      }
    }, 0);
  }

  /**
   * Injects the items into the Atlas topbar
   * @param itemsToInject Items to inject into Atlas topbar
   */
  injectItemsIntoAtlas(itemsToInject: Element[]): void {
    const atlasInjectionContainer = document.getElementsByClassName('business-navbar__atlas-injection')[0];

    // Loop through all the children and append to the atlas inject area
    for (const item of itemsToInject) {
      atlasInjectionContainer.append(item);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!!changes.staticMenu) {
      this.calcStaticMenu();
    }
    if (!!changes.token) {
      this.refreshSession();
    }
    if (!!changes.demo) {
      this.refreshEnvironment();
    }
    if (!!changes.partnerId || !!changes.marketId || !!changes.accountId || !!changes.serviceProviderId ||
      !!changes.brandPath) {
      this.refreshConfig();
    }
    if (!!changes.disableNavigation) {
      this.calcDisableNavigation();
    }
    if (!!changes.alwaysShowMenuToggle) {
      this.calcAlwaysShowMenuToggle();
    }
  }

  ngOnDestroy(): void {
    if (this.sidebar) {
      this.sidebar.destroy();
      this.appRef.detachView(this.sidebar.hostView);
    }
    if (this.snackbar) {
      this.snackbar.destroy();
      this.appRef.detachView(this.snackbar.hostView);
    }
  }

  toggleMenu(): void {
    this.sidebarService.toggleOpen();
  }

  calcStaticMenu(): void {
    this._calculatedStaticMenu = this.staticMenu === 'true' || this.staticMenu === true;
    if (this._calculatedStaticMenu) {
      this.sidebarService.setOpen(false);
    }
    this.sidebarService.setStatic(this._calculatedStaticMenu);
  }

  private refreshSession(): void {
    this.sessionService.setSessionId(this.token);
  }

  private refreshEnvironment(): void {
    this.environmentService.setEnvironment((this.demo === 'true' || this.demo === true) ? Environment.DEMO : Environment.PROD);
  }

  private refreshConfig(): void {
    this.sessionService.sessionId$.pipe(take(1), tap(sessionId => {
      this.cfgService.initialize(this.partnerId, this.marketId, this.accountId, this.brandPath, this.serviceProviderId, sessionId, this.defaultURL);
    })).subscribe();
  }

  private calcDisableNavigation(): void {
    this.sidebarService.setDisabled((this.disableNavigation === 'true' || this.disableNavigation === true));
  }

  private calcAlwaysShowMenuToggle(): void {
    this.sidebarService.setShowMenuToggle((this.alwaysShowMenuToggle === 'true' || this.alwaysShowMenuToggle === true));
  }

  propagateEvent(eventId: string): void {
    this.event.emit(eventId);
  }

  @HostListener('window:keydown', ['$event'])
  _keydown(event: KeyboardEvent): void {
    if (event.keyCode === ENTER && this.modalService.isModalOpen()) {
      event.preventDefault();
    }
    this.keybindService.triggerKeyEvent(event);
  }
}
